import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, Share2 } from "lucide-react";
import { ClinqLogo } from "../ClinqLogo";
import ShareClinqButton from "./ShareClinqButton";
import { PUBLIC_APP_URL } from "../../lib/api";

export const Navbar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const navLinks = [
    { label: "For Business", href: "/for-business" },
    { label: "For Projects", href: "/for-projects" },
    { label: "For Funders", href: "/for-funders" },
    { label: "For Advisors", href: "/for-advisors" },
    { label: "Get Funder-Ready", href: "/why-documents-matter" },
  ];

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <header
      data-testid="landing-navbar"
      className="sticky top-0 z-50 bg-black text-white"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:gap-6 sm:px-6 sm:py-4">
        {/* Logo */}
        <a href="/" data-testid="navbar-logo">
          <ClinqLogo size="lg" tone="teal" testid="navbar-logo-mark" />
        </a>

        {/* Desktop nav links */}
        <ul className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) => (
            <li key={link.label}>
              <a
                href={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-white/80 transition-colors hover:text-white"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden flex-1 lg:block" />

        {/* Desktop auth buttons */}
        <div className="hidden items-center gap-3 md:flex">
          <button
            data-testid="navbar-share-btn"
            aria-label="Share Clinq"
            onClick={async () => {
              const data = { title: "Clinq", text: "Linking African capital to African ventures.", url: PUBLIC_APP_URL };
              if (navigator.share) { try { await navigator.share(data); } catch { /* dismissed */ } }
              else { try { await navigator.clipboard.writeText(data.url); } catch { /* ignore */ } }
            }}
            className="hidden rounded-md border border-white/20 p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white lg:inline-flex"
          >
            <Share2 className="h-4 w-4" />
          </button>
          <button
            data-testid="navbar-login-btn"
            onClick={() => navigate("/login")}
            className="text-sm font-medium text-white/90 transition-colors hover:text-white"
          >
            Log In
          </button>
          <button
            data-testid="navbar-signup-btn"
            onClick={() => navigate("/join")}
            className="rounded-md bg-[#0A8B85] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#066667]"
          >
            Sign Up
          </button>
        </div>

        {/* Mobile menu trigger */}
        <button
          type="button"
          data-testid="navbar-menu-toggle"
          aria-label="Open menu"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className="rounded-lg border border-white/15 p-2 text-white/85 hover:bg-white/5 md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
      </nav>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="navbar-mobile-drawer">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-black/70"
            onClick={() => setOpen(false)}
          />
          <aside className="absolute right-0 top-0 flex h-full w-80 max-w-[85vw] flex-col gap-1 bg-black p-5 shadow-2xl">
            <div className="flex items-center justify-between pb-3">
              <ClinqLogo size="lg" tone="teal" />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="navbar-mobile-drawer-close"
                className="rounded-lg p-2 text-white/70 hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="mt-4 flex flex-col gap-1" aria-label="Mobile primary">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-semibold text-white/85 hover:bg-white/10 hover:text-white"
                >
                  {link.label}
                </a>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-2 border-t border-white/10 pt-4">
              <ShareClinqButton variant="dark" testid="mobile-share-clinq" className="!justify-center" />
              <button
                data-testid="navbar-mobile-login-btn"
                onClick={() => { setOpen(false); navigate("/login"); }}
                className="rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/5"
              >
                Log In
              </button>
              <button
                data-testid="navbar-mobile-signup-btn"
                onClick={() => { setOpen(false); navigate("/join"); }}
                className="rounded-lg bg-[#0A8B85] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#066667]"
              >
                Sign Up
              </button>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
};
